import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { selectAuthFeatureFlags } from '../../../auth/AuthSelectors';
import { ContentClass, SearchFilterContentTypes } from '../../Shared/enums';
import { useConversationalSearch } from '../components/navSearch/NavSearchContext';
import SearchFilterOptions from '../entities/SearchFilterOptions';

const DEFAULT_AI_PLACEHOLDER =
  "Describe what you need... and we'll find what fits...";
const CONV_SEARCH_PATH = '/ai/conv-search/new';

const HYDRATION_MISMATCH_ROUTES = new Set([
  '/video',
  '/audio',
  '/images',
  '/audio/music',
  '/audio/sound-effects',
  '/video/footage',
  '/video/motion-backgrounds',
  '/video/templates',
]);

function isOnHydrationMismatchRoute(): boolean {
  const globalScope = (typeof window === 'undefined' ? global : window) as {
    __CURRENT_PATH__?: string;
  };
  const path = (globalScope.__CURRENT_PATH__ ?? '')
    .split('?')[0]
    .replace(/\/$/, '')
    .toLowerCase();
  return HYDRATION_MISMATCH_ROUTES.has(path);
}

interface UseSearchWithAIOptions {
  showConversationalSearchCta: boolean;
}

interface UseSearchWithAIResult {
  showAiCta: boolean;
  conversationalSearchEnabled: boolean;
  setConversationalSearchEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  getPlaceholder: (
    defaultPlaceholder: string | undefined,
    contentType: SearchFilterContentTypes
  ) => string;
  handleConversationalRedirect: (
    searchTerm: string,
    searchOrigin: string
  ) => boolean;
  isSearchWithAIEnabledForContentType: (contentType: string) => boolean;
}

export function useSearchWithAI({
  showConversationalSearchCta,
}: UseSearchWithAIOptions): UseSearchWithAIResult {
  const { conversationalSearchEnabled, setConversationalSearchEnabled } =
    useConversationalSearch();
  const { showConversationalSearchAudio } = useSelector(selectAuthFeatureFlags);

  const [isHydrating, setIsHydrating] = useState(isOnHydrationMismatchRoute);
  useEffect((): void => setIsHydrating(false), []);
  const showAiCta = !isHydrating && showConversationalSearchCta;

  const isSearchWithAIEnabledForContentType = useCallback(
    (contentType: string): boolean => {
      const contentClass = SearchFilterOptions.getContentClassFromContentType(
        contentType as SearchFilterContentTypes
      );

      const supportedContentClasses = [ContentClass.Video];
      if (showConversationalSearchAudio) {
        supportedContentClasses.push(ContentClass.Audio);
      }
      return supportedContentClasses.includes(contentClass);
    },
    [showConversationalSearchAudio]
  );

  const getPlaceholder = (
    defaultPlaceholder: string | undefined,
    contentType: SearchFilterContentTypes
  ): string => {
    if (!conversationalSearchEnabled) {
      return defaultPlaceholder ?? '';
    }

    const placeholders = {
      [SearchFilterContentTypes.All_videos_content_type]:
        "Describe the scene you're looking for",
      [SearchFilterContentTypes.Footage]:
        "Describe the footage you're looking for",
      [SearchFilterContentTypes.Motion_bgs]: 'Describe the graphic you want',
      [SearchFilterContentTypes.Templates]:
        "Describe the template you're looking for",
      ...(showConversationalSearchAudio && {
        [SearchFilterContentTypes.All_audio_content_type]:
          "Describe the audio you're looking for",
        [SearchFilterContentTypes.Music]:
          "Describe the music you're looking for",
        [SearchFilterContentTypes.Sound_effects]:
          "Describe the sound effect you're looking for",
      }),
    };

    return placeholders[contentType] ?? DEFAULT_AI_PLACEHOLDER;
  };

  const handleConversationalRedirect = (
    searchTerm: string,
    searchOrigin: string
  ): boolean => {
    if (!conversationalSearchEnabled) return false;

    const url = new URL(CONV_SEARCH_PATH, window.location.origin);
    const params = new URLSearchParams({
      search_origin: searchOrigin,
      prompt: searchTerm,
    });
    url.search = params.toString();
    window.location.href = url.toString();
    return true;
  };

  return {
    showAiCta,
    conversationalSearchEnabled,
    setConversationalSearchEnabled,
    getPlaceholder,
    handleConversationalRedirect,
    isSearchWithAIEnabledForContentType,
  };
}
